import React, { useState } from 'react';
import { 
  Car, 
  CreditCard, 
  Shield, 
  Settings, 
  MessageSquare, 
  User,
  Radio,
  QrCode,
  AlertTriangle,
  Database,
  DollarSign,
  Network,
  BarChart3,
  Bell,
  Menu,
  Zap,
  FileText,
  MapPin,
  Activity,
  TrendingUp,
  Globe,
  Building2,
  ChevronDown,
  ChevronUp,
  Users,
  Clock
} from 'lucide-react';
import Footer from './footer';
import Header from './header';


const IssuerDashboard = () => {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (rowIndex) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowIndex]: !prev[rowIndex]
    }));
  };

  const services = [
    {
      id: 'vehicle-classification',
      title: 'Vehicle Classification',
      subtitle: 'Advanced categorization system',
      icon: Car,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      status: 'Active',
      performance: '98.5%',
      details: {
        description: 'Advanced categorization system for vehicles with differential toll rates and lane restriction management',
        features: ['Multi-tier classification', 'Real-time processing', 'Lane restriction enforcement', 'Dynamic toll calculation'],
        metrics: { uptime: '99.8%', transactions: '125K/day', accuracy: '98.5%' }
      }
    },
    {
      id: 'vehicle-registration',
      title: 'Vehicle Registration',
      subtitle: 'Streamlined customer onboarding',
      icon: User,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      status: 'Active',
      performance: '98.5%',
      details: {
        description: 'Streamlined customer onboarding with automated account creation and vehicle-to-user mapping',
        features: ['Automated account creation', 'Document verification', 'Vehicle-user mapping', 'Instant activation'],
        metrics: { uptime: '99.9%', registrations: '2.3K/day', success: '99.2%' }
      }
    },
    {
      id: 'rfid-processing',
      title: 'RFID Processing',
      subtitle: 'High-performance tag reading',
      icon: Radio,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      status: 'Active',
      performance: '99.8%',
      details: {
        description: 'High-performance RFID tag reading and interpretation system with real-time processing',
        features: ['Real-time tag reading', 'Multi-frequency support', 'Anti-collision protocols', 'Signal optimization'],
        metrics: { uptime: '99.9%', reads: '500K/day', accuracy: '99.8%' }
      }
    },
    {
      id: 'rfid-personalization',
      title: 'RFID Personalization',
      subtitle: 'Secure tag customization',
      icon: Settings,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      status: 'Active',
      performance: '97.9%',
      details: {
        description: 'Secure customization and configuration of RFID tags with vehicle-specific data encryption',
        features: ['Data encryption', 'Custom configurations', 'Security protocols', 'Batch processing'],
        metrics: { uptime: '99.5%', personalizations: '1.8K/day', success: '97.9%' }
      }
    },
    {
      id: 'balance-monitoring',
      title: 'Balance Monitoring',
      subtitle: 'Intelligent account tracking',
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      status: 'Active',
      performance: '99.5%',
      details: {
        description: 'Intelligent account balance monitoring with predictive alerts and automated notifications',
        features: ['Predictive alerts', 'Low balance warnings', 'Auto-recharge options', 'Real-time tracking'],
        metrics: { uptime: '99.7%', alerts: '15K/day', accuracy: '99.5%' }
      }
    },
    {
      id: 'settlement-dispute',
      title: 'Settlement & Disputes',
      subtitle: 'Financial transaction management',
      icon: CreditCard,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      status: 'Active',
      performance: '98.9%',
      details: {
        description: 'Comprehensive clearing, settlement, and dispute resolution for inter-bank transactions',
        features: ['Automated clearing', 'Dispute resolution', 'Multi-bank support', 'Reconciliation'],
        metrics: { uptime: '99.3%', settlements: '45K/day', resolution: '98.9%' }
      }
    },
    {
      id: 'core-banking',
      title: 'Core Banking Interface',
      subtitle: 'Secure CBS integration',
      icon: Network,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      status: 'Active',
      performance: '99.7%',
      details: {
        description: 'Secure CBS integration for seamless financial transaction processing and reconciliation',
        features: ['CBS integration', 'Real-time processing', 'Secure protocols', 'Transaction logging'],
        metrics: { uptime: '99.8%', transactions: '200K/day', success: '99.7%' }
      }
    },
    {
      id: 'violation-processing',
      title: 'Violation Processing',
      subtitle: 'Automated traffic management',
      icon: Shield,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      status: 'Active',
      performance: '96.8%',
      details: {
        description: 'Automated traffic violation detection, processing, and exception handling with audit trails',
        features: ['Violation detection', 'Exception handling', 'Audit trails', 'Penalty calculation'],
        metrics: { uptime: '99.1%', violations: '8.5K/day', processed: '96.8%' }
      }
    },
    {
      id: 'security-blacklist',
      title: 'Security Management',
      subtitle: 'Dynamic threat assessment',
      icon: Database,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      status: 'Active',
      performance: '99.1%',
      details: {
        description: 'Dynamic security management with real-time blacklisting and threat assessment capabilities',
        features: ['Real-time blacklisting', 'Threat assessment', 'Security monitoring', 'Risk analysis'],
        metrics: { uptime: '99.9%', threats: '245/day', blocked: '99.1%' }
      }
    },
    {
      id: 'asset-dispute',
      title: 'Asset Management',
      subtitle: 'Inventory & dispute resolution',
      icon: Building2,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      status: 'Active',
      performance: '98.3%',
      details: {
        description: 'Comprehensive inventory management with integrated customer dispute resolution workflows',
        features: ['Inventory tracking', 'Dispute workflows', 'Asset optimization', 'Customer resolution'],
        metrics: { uptime: '99.4%', assets: '12K tracked', disputes: '98.3%' }
      }
    },
    {
      id: 'sgtin-encoding',
      title: 'SGTIN Encoding',
      subtitle: 'Global trade item numbering',
      icon: QrCode,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      status: 'Active',
      performance: '99.4%',
      details: {
        description: 'Advanced Serialized Global Trade Item Number encoding with EPC standards compliance',
        features: ['EPC compliance', 'Serial encoding', 'Standards validation', 'Global compatibility'],
        metrics: { uptime: '99.8%', encodings: '50K/day', compliance: '99.4%' }
      }
    },
    {
      id: 'data-security',
      title: 'Data Security',
      subtitle: 'Cryptographic protection',
      icon: FileText,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      status: 'Active',
      performance: '99.6%',
      details: {
        description: 'Enterprise-grade cryptographic signing, data integrity verification, and barcode processing',
        features: ['Digital signing', 'Data integrity', 'Barcode processing', 'Encryption protocols'],
        metrics: { uptime: '99.9%', signatures: '300K/day', integrity: '99.6%' }
      }
    },
    {
      id: 'netc-interface',
      title: 'NETC Integration',
      subtitle: 'National toll network',
      icon: MapPin,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      status: 'Active',
      performance: '98.7%',
      details: {
        description: 'National Electronic Toll Collection network integration with intelligent routing and mapping',
        features: ['Network integration', 'Intelligent routing', 'Toll mapping', 'Cross-network support'],
        metrics: { uptime: '99.2%', routes: '85K/day', accuracy: '98.7%' }
      }
    },
    {
      id: 'notification-mgmt',
      title: 'Notification System',
      subtitle: 'Multi-channel communication',
      icon: Bell,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      status: 'Active',
      performance: '99.3%',
      details: {
        description: 'Multi-channel customer communication platform with SMS, email, and push notification capabilities',
        features: ['Multi-channel delivery', 'Template management', 'Delivery tracking', 'Personalization'],
        metrics: { uptime: '99.8%', notifications: '180K/day', delivered: '99.3%' }
      }
    },
    {
      id: 'business-intelligence',
      title: 'Business Intelligence',
      subtitle: 'Advanced analytics suite',
      icon: BarChart3,
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      status: 'Active',
      performance: '98.8%',
      details: {
        description: 'Advanced MIS reporting with real-time analytics, dashboards, and predictive insights',
        features: ['Real-time analytics', 'Predictive insights', 'Custom dashboards', 'Report automation'],
        metrics: { uptime: '99.5%', reports: '2.5K/day', accuracy: '98.8%' }
      }
    },
    {
      id: 'account-portal',
      title: 'Account Portal',
      subtitle: 'Self-service interface',
      icon: Menu,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      status: 'Active',
      performance: '97.5%',
      details: {
        description: 'Self-service user interface with account management, transaction history, and settings configuration',
        features: ['Account management', 'Transaction history', 'Settings configuration', 'Mobile responsive'],
        metrics: { uptime: '99.1%', sessions: '25K/day', satisfaction: '97.5%' }
      }
    }
  ];
    const stats = [
    {
      label: "Total Features",
      value: "16",
      icon: Settings,
      color: "blue",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      label: "Active Modules",
      value: "16",
      icon: Zap,
      color: "green",
      gradient: "from-green-500 to-green-600"
    },
    {
      label: "System Uptime",
      value: "99.9%",
      icon: Shield,
      color: "emerald",
      gradient: "from-emerald-500 to-emerald-600"
    },
    {
      label: "Performance",
      value: "Optimal",
      icon: BarChart3,
      color: "purple",
      gradient: "from-purple-500 to-purple-600"
    }
  ];


  // Function to chunk services into rows of 3
  const chunkedServices = [];
  for (let i = 0; i < services.length; i += 3) {
    chunkedServices.push(services.slice(i, i + 3));
  }
  const [activeStep, setActiveStep] = useState(null);

  const steps = [
    {
      id: 1,
      title: "Authorization Request",
      description: "Merchant requests authorization for the transaction",
      direction: "right",
      color: "blue"
    },
    {
      id: 2,
      title: "Authorization Response",
      description: "Issuer responds with approval or decline",
      direction: "left",
      color: "pink"
    },
    {
      id: 3,
      title: "Settlement",
      description: "Funds are transferred between banks",
      direction: "right",
      color: "blue"
    },
    {
      id: 4,
      title: "Chargeback",
      description: "Cardholder disputes the transaction",
      direction: "left",
      color: "pink"
    },
    {
      id: 5,
      title: "Chargeback Response",
      description: "Merchant responds to the chargeback",
      direction: "right",
      color: "blue"
    }
  ];
  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Lightning-fast processing",
      description: "Accelerate reconciliation by processing millions of entries in minutes. Seamlessly integrate data from diverse sources with 100% pre-built connectors, ensuring a smooth and efficient workflow."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enhanced security",
      description: "Bank-grade security protocols protect your financial data with enterprise-level encryption and compliance standards."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Real-time analytics",
      description: "Get instant insights with advanced analytics and reporting tools that help you make data-driven decisions."
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Seamless integration",
      description: "Connect with your existing systems through our robust API integrations and pre-built connectors."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team collaboration",
      description: "Enable your team to work together efficiently with role-based access and collaborative workflows."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 monitoring",
      description: "Round-the-clock system monitoring ensures your operations run smoothly without interruption."
    }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
       <Header/>
       
      {/* Hero Section - Dark */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold mb-6">Intelligent Transportation System</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Advanced Electronic Toll Collection platform with comprehensive vehicle management, 
            financial processing, and regulatory compliance capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Car className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold">Vehicle Management</h3>
              <p className="text-sm text-blue-100 mt-2">Complete vehicle lifecycle management</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <DollarSign className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold">Financial Processing</h3>
              <p className="text-sm text-blue-100 mt-2">Secure payment & settlement systems</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Shield className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold">Compliance & Security</h3>
              <p className="text-sm text-blue-100 mt-2">Advanced violation & dispute management</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <BarChart3 className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold">Analytics & Reporting</h3>
              <p className="text-sm text-blue-100 mt-2">Comprehensive business intelligence</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Light */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive suite of services designed to streamline your transportation operations
            </p>
          </div>
          
          <div className="space-y-8">
            {chunkedServices.map((row, rowIndex) => {
              const isRowExpanded = expandedRows[rowIndex];
              
              return (
                <div key={rowIndex} className="space-y-6">
                  {/* Cards in this row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {row.map((service) => {
                      const IconComponent = service.icon;
                      
                      return (
                        <div
                          key={service.id} 
                          className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden hover:border-gray-300"
                          onClick={() => toggleRow(rowIndex)}
                        >
                          {/* Card Header */}
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className={`w-12 h-12 ${service.bgColor} rounded-lg flex items-center justify-center`}>
                                <IconComponent className={`w-6 h-6 ${service.color}`} />
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                  {service.status}
                                </span>
                                {isRowExpanded ? 
                                  <ChevronUp className="w-4 h-4 text-gray-500 transition-transform duration-200" /> : 
                                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform duration-200" />
                                }
                              </div>
                            </div>
                            
                            <div className="mb-4">
                              <h3 className={`font-semibold text-lg text-gray-900 mb-2`}>{service.title}</h3>
                              <p className="text-sm text-gray-600">{service.subtitle}</p>
                            </div>

                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-gray-500">Performance</span>
                              <span className="font-medium text-gray-900">{service.performance}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`bg-gradient-to-r ${service.color.replace('text-', 'from-').replace('-600', '-400').replace('-700', '-500')} to-indigo-400 h-2 rounded-full transition-all duration-500`}
                                style={{ width: service.performance }}
                              ></div>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <div className={`border-t border-gray-100 transition-all duration-500 ease-in-out ${
                            isRowExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          } overflow-hidden`}>
                            <div className="p-6 space-y-6">
                              <div>
                                <h4 className="font-medium text-gray-900 mb-3 text-sm">Description</h4>
                                <p className="text-sm text-gray-600 leading-relaxed">{service.details.description}</p>
                              </div>

                              <div>
                                <h4 className="font-medium text-gray-900 mb-3 text-sm">Performance Metrics</h4>
                                <div className="grid grid-cols-1 gap-3">
                                  {Object.entries(service.details.metrics).map(([key, value]) => (
                                    <div key={key} className={`${service.bgColor} rounded-lg p-3 flex justify-between items-center`}>
                                      <div className="text-sm text-gray-700 capitalize">{key}</div>
                                      <div className={`text-sm font-semibold ${service.color}`}>{value}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section - Dark */}
      <section className="bg-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Powerful features for modern banking
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Streamline your financial operations with our comprehensive suite of tools designed specifically for banking teams.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700">
                <div className="text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Light */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">System Overview</h2>
            <p className="text-xl text-gray-600">Real-time performance metrics and system status</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={stat.label} className="text-center group">
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${stat.bgColor} rounded-full mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-8 h-8 ${stat.color}`} />
                  </div>
                  
                  <div className="mb-2">
                    <span className={`text-3xl font-bold ${stat.color} block leading-none`}>
                      {stat.value}
                    </span>
                  </div>
                  
                  <h3 className="text-gray-700 font-medium">
                    {stat.label}
                  </h3>
                  
                  <div className={`w-12 h-0.5 ${stat.color.replace('text-', 'bg-')} mx-auto mt-3 rounded-full opacity-30`}></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
       

      {/* Main Content */}
     {/* <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50">
      <h1 className="text-2xl font-bold text-gray-800 mb-8 text-center">
        Acquirer vs. Issuer: Flow
      </h1>
      
      <div className="relative flex items-center justify-between">
         <div className="flex flex-col items-center w-32 z-20">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3 shadow-md">
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Issuer</h2>
          <p className="text-sm text-gray-600 text-center">Cardholder's bank</p>
        </div>

         <div className="flex-1 px-8 relative">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={step.id} className="relative">
                 <div className="absolute inset-0 flex items-center">
                  {step.direction === "right" ? (
                    <>
                       <div className={`absolute left-0 right-0 h-0.5 ${step.color === 'blue' ? 'bg-blue-400' : 'bg-pink-400'}`}></div>
                       <div className={`absolute right-0 w-0 h-0 border-l-8 border-t-4 border-b-4 border-transparent ${step.color === 'blue' ? 'border-l-blue-400' : 'border-l-pink-400'}`}></div>
                    </>
                  ) : (
                    <>
                       <div className={`absolute left-0 right-0 h-0.5 ${step.color === 'blue' ? 'bg-blue-400' : 'bg-pink-400'}`}></div>
                       <div className={`absolute left-0 w-0 h-0 border-r-8 border-t-4 border-b-4 border-transparent ${step.color === 'blue' ? 'border-r-blue-400' : 'border-r-pink-400'}`}></div>
                    </>
                  )}
                </div>

                 <div className="relative z-10 flex justify-center">
                  <div 
                    className={`flex items-center px-4 py-2 rounded-full cursor-pointer transition-all duration-200 shadow-md bg-white border-2 ${
                      step.color === 'blue' 
                        ? 'border-blue-200 hover:border-blue-300' 
                        : 'border-pink-200 hover:border-pink-300'
                    } ${activeStep === step.id ? 'shadow-lg scale-105' : ''}`}
                    onClick={() => setActiveStep(activeStep === step.id ? null : step.id)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0 ${
                      step.color === 'blue' ? 'bg-blue-500' : 'bg-pink-500'
                    }`}>
                      {step.id}
                    </div>
                    <span className="text-sm font-medium text-gray-800 whitespace-nowrap">
                      {step.title}
                    </span>
                  </div>
                </div>
                
                 {activeStep === step.id && (
                  <div className="flex justify-center mt-3">
                    <div className="p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-sm text-gray-600 max-w-sm z-20 relative">
                      {step.description}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

         <div className="flex flex-col items-center w-32 z-20">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-3 shadow-md">
            <Building2 className="w-8 h-8 text-pink-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Acquirer</h2>
          <p className="text-sm text-gray-600 text-center">Merchant's bank</p>
        </div>
      </div>
    </div> */}
         <Footer/>
    </div>
    
  );
};

export default IssuerDashboard;